import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

const ModalContainer = ({
  isOpen,
  onClose,
  children,
  closeOnBackdrop = true,
  closeOnEscape = true,
  className = "",
  backdropClassName = "",
  animationDuration = 400,
  maxWidth = "max-w-md",
}) => {
  const { i18n } = useTranslation();
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);
  const isAnimatingRef = useRef(false);
  const direction = i18n.dir();

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement;
      document.body.style.overflow = "hidden";

      // Focus the modal after animation
      setTimeout(() => {
        if (modalRef.current) {
          const firstFocusable = modalRef.current.querySelector(
            'button, [href], input, select, textarea, [tabindex]:not([tabIndex="-1"])'
          );
          if (firstFocusable) {
            firstFocusable.focus();
          } else {
            modalRef.current.focus();
          }
        }
      }, animationDuration);
    } else {
      document.body.style.overflow = "";
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, animationDuration]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (closeOnEscape && event.key === "Escape" && !isAnimatingRef.current) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, closeOnEscape, onClose]);

  const handleBackdropClick = (event) => {
    if (
      closeOnBackdrop &&
      event.target === event.currentTarget &&
      !isAnimatingRef.current
    ) {
      onClose();
    }
  };

  const handleAnimationStart = () => {
    isAnimatingRef.current = true;
  };

  const handleAnimationEnd = () => {
    isAnimatingRef.current = false;
  };

  const trapFocus = (event) => {
    if (!modalRef.current) return;

    const focusableElements = modalRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabIndex="-1"])'
    );

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.key === "Tab") {
      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className={`
        fixed inset-0 z-1000 flex items-center justify-center p-4
        ${backdropClassName}
      `}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      dir={direction}
      lang={i18n.language}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-secondary-900/50 modal-backdrop animate-fade-in"
        aria-hidden="true"
      />

      {/* Modal Content */}
      <div
        ref={modalRef}
        className={`
          relative w-full ${maxWidth} bg-background rounded-lg shadow-modal
          overflow-y-auto max-h-[90vh]
          animate-modal-enter
          ${className}
        `}
        onKeyDown={trapFocus}
        onAnimationStart={handleAnimationStart}
        onAnimationEnd={handleAnimationEnd}
        tabIndex={-1}
      >
        {children}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ModalContainer;


